import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
  X,
  TrendingUp,
  DollarSign,
  Workflow,
  ClipboardCheck,
} from 'lucide-react';

/* ─── GraphQL ──────────────────────────────────────────────────────── */

const SHAREABLE_SNAPSHOTS = gql`
  query ShareableSnapshotsForPicker {
    currentUser {
      id
      shareable_snapshots {
        id
        type
        label
        created_at
        is_primary
        preview_metrics
      }
    }
  }
`;

const SHARE_SNAPSHOT_WITH_DEAL_TEAM = gql`
  mutation ShareSnapshotWithDealTeam(
    $deal_team_id: ID!
    $source_type: String!
    $source_id: ID!
    $note: String
    $shared_with_roles: [String!]
  ) {
    share_snapshot_with_deal_team(
      deal_team_id: $deal_team_id
      source_type: $source_type
      source_id: $source_id
      note: $note
      shared_with_roles: $shared_with_roles
    ) {
      vault_document {
        id
      }
      errors
    }
  }
`;

/* ─── Constants ────────────────────────────────────────────────────── */

const ROLE_OPTIONS = [
  { value: 'cbv', label: 'Valuation Specialist (CBV)' },
  { value: 'business_broker', label: 'Business Broker' },
  { value: 'lawyer', label: 'Lawyer' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'lender', label: 'Lender' },
  { value: 'cepa_coach', label: 'CEPA Coach' },
];

// One icon + a "start one" link target per snapshot type. Used both for
// the picker card and the empty-state placeholder so the modal stays
// visually consistent between filled and empty sections.
const SNAPSHOT_TYPE_CONFIG = {
  valuation_snapshot: {
    label: 'Valuation',
    icon: TrendingUp,
    emptyMessage: "You haven't created a Valuation yet.",
    emptyLink: '/succeedo/valuation',
  },
  financing_snapshot: {
    label: 'Financing',
    icon: DollarSign,
    emptyMessage: "You haven't created a Financing scenario yet.",
    emptyLink: '/succeedo/financing-calculator',
  },
  deal_structure_snapshot: {
    label: 'Deal Structure',
    icon: Workflow,
    emptyMessage: "You haven't created a Deal Structure yet.",
    emptyLink: '/succeedo/deal-structure',
  },
  assessment_snapshot: {
    label: 'Assessment',
    icon: ClipboardCheck,
    emptyMessage: "You haven't created an Assessment yet.",
    emptyLink: '/succeedo/assessment',
  },
};

const SNAPSHOT_TYPES = [
  'valuation_snapshot',
  'financing_snapshot',
  'deal_structure_snapshot',
  'assessment_snapshot',
];

/* ─── Helpers ──────────────────────────────────────────────────────── */

function previewLines(metrics) {
  if (!metrics || typeof metrics !== 'object') return [];
  // ShareableSnapshotPreview returns { primary, secondary, tertiary } strings.
  return [metrics.primary, metrics.secondary, metrics.tertiary].filter(Boolean);
}

/* ─── PreselectedSnapshotCard ──────────────────────────────────────── */

// Read-only summary card for the preselected-mode header — visually mirrors
// the picker's selected state so the modal looks coherent across both modes.
const PreselectedSnapshotCard = ({ snapshot }) => {
  const cfg = SNAPSHOT_TYPE_CONFIG[snapshot.type] || {};
  const Icon = cfg.icon || TrendingUp;
  const lines = previewLines(snapshot.preview_metrics);

  return (
    <div className="p-4 rounded-[12px] border-2 border-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/5">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[hsl(var(--succeedo-orange))]/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-[hsl(var(--succeedo-orange))]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium text-[hsl(var(--foreground))] truncate">
            {snapshot.label || cfg.label || 'Snapshot'}
          </div>
          {snapshot.is_primary && (
            <div className="font-mono text-[10px] uppercase text-[hsl(var(--succeedo-orange))] mt-0.5">
              Primary
            </div>
          )}
        </div>
      </div>
      {lines.length > 0 && (
        <div className="space-y-1">
          {lines.map((line) => (
            <div
              key={`${snapshot.id}:${line}`}
              className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

PreselectedSnapshotCard.propTypes = {
  snapshot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string,
    is_primary: PropTypes.bool,
    preview_metrics: PropTypes.object,
  }).isRequired,
};

/* ─── ShareSnapshotModal ───────────────────────────────────────────── */

const ShareSnapshotModal = ({
  open,
  onClose,
  onSuccess,
  dealTeamId,
  dealTeamName,
  preselectedSnapshot,
}) => {
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);
  const [note, setNote] = useState('');
  const [restrictByRole, setRestrictByRole] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Preselected mode: when the modal is opened from inside a tool (Valuation,
  // Financing, Deal Structure) we already know which snapshot is being
  // shared — skip the picker query AND the picker UI. Toggled by passing
  // a `preselectedSnapshot` prop with at least { id, type }.
  const isPreselected = !!preselectedSnapshot?.id;

  // Only fetch when the modal is open AND we're in picker mode.
  // cache-and-network keeps the picker current if the user opens, creates a
  // snapshot in another tab, comes back.
  const { data, loading } = useQuery(SHAREABLE_SNAPSHOTS, {
    skip: !open || isPreselected,
    fetchPolicy: 'cache-and-network',
  });

  const [shareSnapshot] = useMutation(SHARE_SNAPSHOT_WITH_DEAL_TEAM);

  // Mirror DealTeamUploadModal: reset every time the modal closes — including
  // after a successful submit — so the next open is clean.
  useEffect(() => {
    if (!open) {
      setSelectedSnapshotId(null);
      setNote('');
      setRestrictByRole(false);
      setSelectedRoles([]);
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const snapshots = data?.currentUser?.shareable_snapshots || [];

  // Resolve the snapshot we're submitting:
  //   - Preselected mode → use the prop directly
  //   - Picker mode → look up the user's selection in the fetched list
  const selectedSnapshot = isPreselected
    ? preselectedSnapshot
    : snapshots.find((s) => s.id === selectedSnapshotId) || null;

  const toggleRole = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const canShare =
    !!selectedSnapshot &&
    !submitting &&
    !(restrictByRole && selectedRoles.length === 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canShare) return;

    setSubmitting(true);
    setError(null);

    try {
      const trimmedNote = note.trim();
      const rolesToShare = restrictByRole ? selectedRoles : [];

      const result = await shareSnapshot({
        variables: {
          deal_team_id: dealTeamId,
          source_type: selectedSnapshot.type,
          source_id: selectedSnapshot.id,
          note: trimmedNote || null,
          shared_with_roles: rolesToShare,
        },
      });

      const payload = result.data?.share_snapshot_with_deal_team;
      const errs = payload?.errors || [];
      if (errs.length > 0) throw new Error(errs.join(', '));
      if (!payload?.vault_document?.id) {
        throw new Error('Share did not return a document id.');
      }

      if (window.ahoy) {
        window.ahoy.track('succeedo_deal_team_snapshot_shared', {
          deal_team_id: dealTeamId,
          source_type: selectedSnapshot.type,
          has_note: trimmedNote.length > 0,
          shared_with_roles: rolesToShare,
        });
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Share failed. Please try again.');
      setSubmitting(false);
    }
  };

  // Build the picker grid: for each of the 4 snapshot types, render either
  // the user's snapshots of that type or an empty-state placeholder card.
  const cards = [];
  SNAPSHOT_TYPES.forEach((type) => {
    const ofType = snapshots.filter((s) => s.type === type);
    if (ofType.length === 0) {
      cards.push({ kind: 'empty', type });
    } else {
      ofType.forEach((snap) => cards.push({ kind: 'snap', snapshot: snap }));
    }
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deal-team-share-snapshot-title">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[hsl(var(--border))]">
          <div>
            <h2
              id="deal-team-share-snapshot-title"
              className="font-serif text-[20px] font-light text-[hsl(var(--foreground))]">
              {isPreselected ? 'Share with your team' : 'Share a snapshot'}
            </h2>
            <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5">
              {isPreselected
                ? 'Send this snapshot to your deal team workspace'
                : "Pick from snapshots you've created in Succeedo"}
              {dealTeamName ? ` · ${dealTeamName}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {isPreselected ? (
            // Preselected mode — show a single read-only confirmation card
            // for the snapshot being shared. No picker, no query.
            <div className="space-y-3">
              <label className="block font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                Sharing
              </label>
              <PreselectedSnapshotCard snapshot={preselectedSnapshot} />
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                Select snapshot
              </label>

              {loading && snapshots.length === 0 ? (
                <div className="text-[13px] text-[hsl(var(--muted-foreground))] py-4">
                  Loading your snapshots…
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {cards.map((card) => {
                  if (card.kind === 'empty') {
                    const cfg = SNAPSHOT_TYPE_CONFIG[card.type];
                    const Icon = cfg.icon;
                    return (
                      <div
                        key={`empty-${card.type}`}
                        className="p-4 rounded-[12px] border-2 border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-[hsl(var(--muted))] flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-[hsl(var(--muted-foreground))]/60" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-[hsl(var(--muted-foreground))]">
                              {cfg.label}
                            </div>
                          </div>
                        </div>
                        <p className="text-[12px] text-[hsl(var(--muted-foreground))]/70 mb-2">
                          {cfg.emptyMessage}
                        </p>
                        <a
                          href={cfg.emptyLink}
                          className="text-[11px] text-[hsl(var(--succeedo-orange))] hover:underline">
                          Start one →
                        </a>
                      </div>
                    );
                  }

                  const snap = card.snapshot;
                  const cfg = SNAPSHOT_TYPE_CONFIG[snap.type] || {};
                  const Icon = cfg.icon || TrendingUp;
                  const isSelected = selectedSnapshotId === snap.id;
                  const lines = previewLines(snap.preview_metrics);

                  return (
                    <button
                      type="button"
                      key={snap.id}
                      onClick={() => setSelectedSnapshotId(snap.id)}
                      className={`p-4 rounded-[12px] border-2 transition-all text-left ${
                        isSelected
                          ? 'ring-2 ring-[hsl(var(--succeedo-orange))] border-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/5'
                          : 'border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:border-[hsl(var(--muted-foreground))]/30'
                      }`}>
                      <div className="flex items-start gap-3 mb-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? 'bg-[hsl(var(--succeedo-orange))]/10'
                              : 'bg-[hsl(var(--muted))]'
                          }`}>
                          <Icon
                            className={`w-4 h-4 ${
                              isSelected
                                ? 'text-[hsl(var(--succeedo-orange))]'
                                : 'text-[hsl(var(--muted-foreground))]'
                            }`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-[hsl(var(--foreground))] truncate">
                            {snap.label}
                          </div>
                          {snap.is_primary && (
                            <div className="font-mono text-[10px] uppercase text-[hsl(var(--succeedo-orange))] mt-0.5">
                              Primary
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1">
                        {lines.map((line) => (
                          <div
                            key={`${snap.id}:${line}`}
                            className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
                            {line}
                          </div>
                        ))}
                      </div>
                    </button>
                  );
                })}
                </div>
            )}
            </div>
          )}

          {/* Note (Polymet `commentary` → `note` per Locked Decisions) */}
          <div className="space-y-1.5">
            <label
              htmlFor="dealteam-share-snapshot-note"
              className="block text-[12px] font-mono uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
              Note (optional)
            </label>
            <textarea
              id="dealteam-share-snapshot-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              rows={3}
              placeholder="What changed in this version, or what should the team focus on?"
              className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[14px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/30 resize-none"
            />
            <button
              type="button"
              onClick={() => setNote('')}
              disabled={submitting || note.length === 0}
              className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] disabled:opacity-40 disabled:cursor-not-allowed">
              Skip note
            </button>
          </div>

          {/* Restrict by role (mirrors DealTeamUploadModal exactly) */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={restrictByRole}
                onChange={(e) => setRestrictByRole(e.target.checked)}
                disabled={submitting}
                className="w-4 h-4 rounded border-[hsl(var(--border))] accent-[hsl(var(--succeedo-orange))]"
              />
              <span className="text-[13px] text-[hsl(var(--foreground))]">
                Restrict access by role
              </span>
            </label>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 ml-6">
              {restrictByRole
                ? 'Only checked roles will see this snapshot.'
                : 'All team members will be able to view this snapshot.'}
            </p>

            {restrictByRole && (
              <div className="mt-3 ml-6 grid grid-cols-1 gap-1.5">
                {ROLE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(opt.value)}
                      onChange={() => toggleRole(opt.value)}
                      disabled={submitting}
                      className="w-4 h-4 rounded border-[hsl(var(--border))] accent-[hsl(var(--succeedo-orange))]"
                    />
                    <span className="text-[13px] text-[hsl(var(--foreground))]">
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[hsl(var(--border))]">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-[13px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canShare}
              className="px-4 py-2 rounded-full text-[13px] font-medium bg-[hsl(var(--succeedo-orange))] text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity">
              {submitting ? 'Sharing…' : 'Share with deal team'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ShareSnapshotModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  dealTeamId: PropTypes.string.isRequired,
  dealTeamName: PropTypes.string,
  // When provided, skips the picker and shares this snapshot directly.
  // Shape: { id, type, label?, is_primary?, preview_metrics? }
  preselectedSnapshot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string,
    is_primary: PropTypes.bool,
    preview_metrics: PropTypes.object,
  }),
};

ShareSnapshotModal.defaultProps = {
  onSuccess: undefined,
  dealTeamName: null,
  preselectedSnapshot: null,
};

export default ShareSnapshotModal;
